import { useState, useEffect, useCallback, useRef } from "react";
import { supabase } from "@/lib/supabaseClient";
import { uploadFileToVps, deleteFileFromVps } from "@/lib/vpsStorage";
import Swal from "sweetalert2";

interface Photo {
  id: string;
  photo_url: string;
  sort_order: number;
}

interface Props {
  bookId: string;
  tenantId: string;
  coverPhotoUrl: string | null;
  onCoverChange: (url: string | null) => void;
}

export default function PhotosTab({ bookId, tenantId, coverPhotoUrl, onCoverChange }: Props) {
  const [photos,   setPhotos]   = useState<Photo[]>([]);
  const [loading,  setLoading]  = useState(true);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [pendingFileId, setPendingFileId] = useState<string | null>(null);

  const fetchPhotos = useCallback(async () => {
    setLoading(true);
    const { data } = await supabase
      .from("signature_book_photos")
      .select("*")
      .eq("signature_book_id", bookId)
      .order("sort_order");
    setPhotos((data as Photo[]) ?? []);
    setLoading(false);
  }, [bookId]);

  useEffect(() => { fetchPhotos(); }, [fetchPhotos]);

  const handleUpload = async (file: File) => {
    if (!tenantId || !bookId) return;
    setUploading(true);
    setPendingFileId(null);
    try {
      const result = await uploadFileToVps({
        file,
        tenantId,
        entityType: "signature_book_photo",
        entityId: bookId,
        folder: "signature_books",
        allowedTypes: ["image/jpeg", "image/png", "image/webp", "image/gif"],
        maxSizeMB: 10,
      });
      if (result.success && result.url) {
        setPendingFileId(result.fileId ?? null);
        const nextOrder = photos.length > 0 ? Math.max(...photos.map((p) => p.sort_order)) + 1 : 0;
        const { error } = await supabase.from("signature_book_photos").insert({
          tenant_id: tenantId,
          signature_book_id: bookId,
          photo_url: result.url,
          sort_order: nextOrder,
        });
        if (error) {
          // Cleanup: delete uploaded file if DB insert failed
          if (result.fileId) {
            await deleteFileFromVps({ fileId: result.fileId, tenantId, silent: true });
          }
          Swal.fire({ title: "Error", text: error.message, icon: "error" });
        } else {
          fetchPhotos();
        }
      } else {
        Swal.fire({ title: "Error", text: result.error || "Upload failed", icon: "error" });
      }
    } catch (err) {
      Swal.fire({ title: "Error", text: err instanceof Error ? err.message : "Upload failed", icon: "error" });
    } finally {
      setUploading(false);
      setPendingFileId(null);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleUpload(file);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleUpload(file);
  };

  const handleDelete = async (photo: Photo) => {
    const { isConfirmed } = await Swal.fire({
      title: "¿Eliminar foto?",
      text: "Se eliminarán también los mensajes anclados a esta foto.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sí, eliminar",
      cancelButtonText: "Cancelar",
    });
    if (!isConfirmed) return;

    // Delete from VPS if we have a record
    const { data: fileRecord } = await supabase
      .from("uploaded_files")
      .select("id")
      .eq("file_url", photo.photo_url)
      .eq("tenant_id", tenantId)
      .maybeSingle();
    if (fileRecord?.id) {
      await deleteFileFromVps({ fileId: fileRecord.id, tenantId, silent: true });
    }

    await supabase.from("signature_book_photos").delete().eq("id", photo.id);
    if (coverPhotoUrl === photo.photo_url) onCoverChange(null);
    fetchPhotos();
  };

  const handleSetCover = async (photo: Photo) => {
    const { error } = await supabase
      .from("signature_books")
      .update({ cover_photo_url: photo.photo_url, updated_at: new Date().toISOString() })
      .eq("id", bookId);
    if (!error) {
      onCoverChange(photo.photo_url);
      Swal.fire({ title: "Portada actualizada", icon: "success", timer: 1200, showConfirmButton: false });
    }
  };

  const handleMoveUp = async (photo: Photo, idx: number) => {
    if (idx === 0) return;
    const prev = photos[idx - 1];
    await Promise.all([
      supabase.from("signature_book_photos").update({ sort_order: prev.sort_order }).eq("id", photo.id),
      supabase.from("signature_book_photos").update({ sort_order: photo.sort_order }).eq("id", prev.id),
    ]);
    fetchPhotos();
  };

  const handleMoveDown = async (photo: Photo, idx: number) => {
    if (idx === photos.length - 1) return;
    const next = photos[idx + 1];
    await Promise.all([
      supabase.from("signature_book_photos").update({ sort_order: next.sort_order }).eq("id", photo.id),
      supabase.from("signature_book_photos").update({ sort_order: photo.sort_order }).eq("id", next.id),
    ]);
    fetchPhotos();
  };

  return (
    <div className="space-y-6 max-w-3xl">
      {/* Upload photo */}
      <div className="bg-white rounded-2xl border border-slate-200 p-5">
        <h3 className="text-slate-700 font-semibold text-sm mb-3 flex items-center gap-2">
          <span className="w-5 h-5 flex items-center justify-center text-amber-500"><i className="ri-add-circle-line"></i></span>
          Agregar foto
        </h3>
        <div
          onDrop={handleDrop}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onClick={() => fileInputRef.current?.click()}
          className={`border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-all ${
            dragOver ? "border-amber-400 bg-amber-50" : "border-slate-200 hover:border-slate-300 hover:bg-slate-50"
          }`}
        >
          {uploading ? (
            <div className="flex items-center gap-2 text-slate-500 text-sm">
              <div className="w-4 h-4 border-2 border-amber-400 border-t-transparent rounded-full animate-spin" />
              Subiendo…
            </div>
          ) : (
            <>
              <div className="w-10 h-10 mx-auto mb-2 flex items-center justify-center rounded-xl bg-amber-100 text-amber-400">
                <i className="ri-upload-cloud-2-line text-xl"></i>
              </div>
              <p className="text-slate-500 text-sm font-medium">Click o arrastra para subir foto</p>
              <p className="text-slate-400 text-xs mt-0.5">JPG, PNG, WEBP</p>
            </>
          )}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileChange}
            disabled={uploading}
            className="hidden"
          />
        </div>
        <p className="text-xs text-slate-400 mt-2">Las fotos se suben al VPS y se guardan automáticamente en el libro.</p>
      </div>

      {/* Cover upload */}
      <div className="bg-white rounded-2xl border border-slate-200 p-5">
        <h3 className="text-slate-700 font-semibold text-sm mb-3 flex items-center gap-2">
          <span className="w-5 h-5 flex items-center justify-center text-amber-500"><i className="ri-star-line"></i></span>
          Portada del Libro
        </h3>
        <div
          onDrop={(e) => {
            e.preventDefault();
            const file = e.dataTransfer.files?.[0];
            if (file) handleUpload(file);
          }}
          onDragOver={(e) => e.preventDefault()}
          onClick={() => fileInputRef.current?.click()}
          className="cursor-pointer"
        >
          {coverPhotoUrl ? (
            <div className="relative rounded-xl overflow-hidden border border-slate-200 h-40 bg-slate-100">
              <img src={coverPhotoUrl} alt="Cover" className="w-full h-full object-cover object-top" />
              <button
                onClick={(e) => { e.stopPropagation(); onCoverChange(null); }}
                className="absolute top-2 right-2 w-7 h-7 flex items-center justify-center bg-black/50 hover:bg-red-500 text-white rounded-lg transition-colors cursor-pointer"
              >
                <i className="ri-delete-bin-line text-xs"></i>
              </button>
              <div className="absolute bottom-2 left-2 flex items-center gap-1 bg-amber-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full">
                <i className="ri-star-fill text-[8px]"></i> Portada activa
              </div>
            </div>
          ) : (
            <div className="border-2 border-dashed border-slate-200 rounded-xl p-6 text-center">
              <div className="w-10 h-10 mx-auto mb-2 flex items-center justify-center rounded-xl bg-slate-100 text-slate-400">
                <i className="ri-image-line text-xl"></i>
              </div>
              <p className="text-slate-500 text-sm font-medium">Sin portada</p>
              <p className="text-slate-400 text-xs mt-0.5">Sube una foto y márcala como portada</p>
            </div>
          )}
        </div>
      </div>

      {/* Photos grid */}
      {loading ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {[1,2,3,4,5,6].map((i) => <div key={i} className="aspect-square rounded-xl bg-slate-100 animate-pulse" />)}
        </div>
      ) : photos.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-center bg-white rounded-2xl border border-dashed border-slate-200">
          <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-amber-100 text-amber-400 mb-3">
            <i className="ri-image-2-line text-2xl"></i>
          </div>
          <p className="text-slate-500 font-medium text-sm">Sin fotos aún</p>
          <p className="text-slate-400 text-xs mt-1">Sube fotos arriba</p>
        </div>
      ) : (
        <>
          <p className="text-xs text-slate-400">{photos.length} {photos.length === 1 ? "foto" : "fotos"} · Usa las flechas para ordenar</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {photos.map((photo, idx) => (
              <div
                key={photo.id}
                className={`relative group rounded-xl overflow-hidden bg-slate-100 border-2 transition-all ${
                  coverPhotoUrl === photo.photo_url ? "border-amber-400" : "border-transparent"
                }`}
              >
                <div className="aspect-square">
                  <img src={photo.photo_url} alt="" className="w-full h-full object-cover object-top" />
                </div>

                {/* Cover badge */}
                {coverPhotoUrl === photo.photo_url && (
                  <div className="absolute top-2 left-2 flex items-center gap-1 bg-amber-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full">
                    <i className="ri-star-fill text-[8px]"></i> Portada
                  </div>
                )}

                {/* Sort overlay */}
                <div className="absolute top-2 right-2 flex flex-col gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button
                    onClick={() => handleMoveUp(photo, idx)}
                    disabled={idx === 0}
                    className="w-6 h-6 flex items-center justify-center bg-white/90 rounded-lg text-slate-600 hover:bg-white disabled:opacity-30 cursor-pointer shadow"
                  >
                    <i className="ri-arrow-up-s-line text-xs"></i>
                  </button>
                  <button
                    onClick={() => handleMoveDown(photo, idx)}
                    disabled={idx === photos.length - 1}
                    className="w-6 h-6 flex items-center justify-center bg-white/90 rounded-lg text-slate-600 hover:bg-white disabled:opacity-30 cursor-pointer shadow"
                  >
                    <i className="ri-arrow-down-s-line text-xs"></i>
                  </button>
                </div>

                {/* Bottom actions */}
                <div className="absolute bottom-0 left-0 right-0 flex items-center gap-1 p-2 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                  <button
                    onClick={() => handleSetCover(photo)}
                    disabled={coverPhotoUrl === photo.photo_url}
                    className="flex-1 text-[10px] font-semibold py-1 rounded-lg bg-amber-500/80 hover:bg-amber-500 text-white disabled:opacity-50 cursor-pointer whitespace-nowrap transition-colors"
                  >
                    {coverPhotoUrl === photo.photo_url ? "Portada" : "Set portada"}
                  </button>
                  <button
                    onClick={() => handleDelete(photo)}
                    className="w-6 h-6 flex items-center justify-center bg-red-500/80 hover:bg-red-500 rounded-lg text-white cursor-pointer transition-colors"
                  >
                    <i className="ri-delete-bin-line text-xs"></i>
                  </button>
                </div>

                {/* Index badge */}
                <div className="absolute bottom-2 right-2 opacity-0 group-hover:opacity-0 w-5 h-5 flex items-center justify-center bg-black/40 rounded-full text-white text-[10px] font-bold">
                  {idx + 1}
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}